import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Book, Trash2, Archive, ShieldCheck, ArchiveRestore, ShieldOff } from 'lucide-react';
import { ApiProvider } from '@/types/sharedTypes';
import { generateAndSaveHighlightsUnified, deleteHighlight, getHighlightsByPoemId } from '@/services/highlightService';
import { 
  getPoemById as getPoemByIdService, 
  getAllPoems, 
  deletePoemById, 
  togglePoemArchiveStatus, 
  togglePoemProtectStatus 
} from '@/services/poemService';
import { getPoetById as getPoetByIdService } from '@/services/poetService';
import { buildHighlightPrompt } from '@/utils/highlightPromptBuilder';
import { getHighlightLogger, downloadHighlightLogs, getHighlightLogsAsString } from '@/utils/logging/browserLogger';
import type { Database } from '@/types/supabase';
import HighlightGenerationStatus from '@/components/highlight/HighlightGenerationStatus';
import { useHighlightGeneration } from '@/hooks/useHighlightGeneration';
import { Link } from 'react-router-dom';
import { AppError, handleError, ErrorType } from '@/utils/error/errorHandler';
import type { ServiceResult } from '@/utils/service/serviceUtils';
import { Switch } from "@/components/ui/switch";

type Poem = Database['public']['Tables']['poems']['Row'];
type Poet = Database['public']['Tables']['poets']['Row'];
type PoemDetails = Database['public']['Views']['poem_details']['Row'] & {
  is_protected?: boolean | null;
  highlights?: any | null;
  system_prompt?: string | null;
  updated_at?: string | null;
};

const AdminHighlightRegeneration = () => {
  const { toast } = useToast();
  const [searchType, setSearchType] = useState<'id' | 'title' | 'poet'>('id');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PoemDetails[]>([]);
  const [selectedPoem, setSelectedPoem] = useState<PoemDetails | null>(null);
  const [aiProvider, setAiProvider] = useState<ApiProvider>(ApiProvider.ANTHROPIC);
  const [excludeArchived, setExcludeArchived] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState({
    search: false,
    regenerate: false,
    fetchStylePoints: false,
    fetchHighlightCount: false,
    deletePoem: false,
    toggleArchive: false,
    toggleProtect: false,
  });
  const [poetStylePoints, setPoetStylePoints] = useState<string[]>([]);
  const [currentHighlightCount, setCurrentHighlightCount] = useState<number | null>(null);
  
  // --- NEW: State to trigger search effect --- 
  const [searchTrigger, setSearchTrigger] = useState(0);
  // --- Store search parameters separately for the effect --- 
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [currentSearchType, setCurrentSearchType] = useState<'id' | 'title' | 'poet'>('id');
  
  const logger = getHighlightLogger('admin');
  
  useEffect(() => {
    logger.info('Admin highlight regeneration page initialized', {
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE
    });
    
    return () => {
      logger.info('Admin highlight regeneration page closed');
    };
  }, []);

  useEffect(() => {
    if (selectedPoem?.poet_id) {
      fetchPoetStylePoints(selectedPoem.poet_id);
    } else {
      setPoetStylePoints([]);
    }
    if (selectedPoem?.id) {
        getHighlightCount(selectedPoem.id);
    } else {
        setCurrentHighlightCount(null);
    }
  }, [selectedPoem]);

  // Reset pagination if the archive filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [excludeArchived]);

  const fetchPoetStylePoints = async (poetId: string) => {
    setLoading(prev => ({ ...prev, fetchStylePoints: true }));
    try {
      logger.info('Fetching poet style points via service', { poetId });

      const poetResult: ServiceResult<Poet> = await getPoetByIdService(poetId);

      if (poetResult.error) {
        throw poetResult.error;
      }

      const poetData = poetResult.data;

      if (!poetData) {
        throw new AppError(`Poet data is null for ID ${poetId} despite no error.`, 'UNEXPECTED');
      }

      let points: string[] = [];
      const rawStylePoints = poetData.style_points as any;

      if (rawStylePoints) {
        if (Array.isArray(rawStylePoints)) {
          points = rawStylePoints.filter((point): point is string => typeof point === 'string');
        } else if (typeof rawStylePoints === 'object' && rawStylePoints !== null) {
           points = Object.values(rawStylePoints)
             .flat()
             .filter((point): point is string => typeof point === 'string');
        }
      }

      setPoetStylePoints(points);

      logger.info('Poet style points retrieved via service', {
        poetId,
        pointCount: points.length,
        stylePoints: points
      });
    } catch (error) {
      console.error('Error fetching poet style points via service:', error);
      logger.error('Failed to fetch poet style points via service', error, { poetId });
      toast({
        title: "Error",
        description: "Failed to fetch poet style information.",
        variant: "destructive"
      });
       setPoetStylePoints([]);
    } finally {
       setLoading(prev => ({ ...prev, fetchStylePoints: false }));
    }
  };

  // --- NEW: useEffect to handle actual data fetching/filtering for search --- 
  useEffect(() => {
    // Only run if triggered and query exists (or search type is not ID)
    if (searchTrigger === 0 || (!currentSearchQuery.trim() && currentSearchType !== 'id')) {
      // Optionally clear results if query is cleared and trigger is 0 (initial load)
      if (searchTrigger === 0) setSearchResults([]);
      return;
    }

    const performSearch = async () => {
      setLoading(prev => ({ ...prev, search: true }));
      setSelectedPoem(null); // Reset selection on new search
      // Don't reset pagination here, handle it in UI event if needed

      logger.info('Performing poem search via effect', {
        searchType: currentSearchType,
        searchQuery: currentSearchQuery,
        searchTrigger, // Log trigger count
      });
      
      try {
        let fetchedPoems: PoemDetails[] = [];

        if (currentSearchType === 'id') {
          if (!currentSearchQuery.trim()) {
             // Skip ID search if query is empty
             setSearchResults([]);
             throw new AppError('Poem ID cannot be empty.', ErrorType.VALIDATION); 
          }
          const poem = await getPoemByIdService(currentSearchQuery.trim());
          if (poem && poem.id) {
            fetchedPoems = [poem as unknown as PoemDetails];
          } else {
            fetchedPoems = [];
          }
        } else {
          // Fetch all poems only when needed (non-ID search)
          const allPoemsData = await getAllPoems({ includeArchived: true });

          if (!Array.isArray(allPoemsData)) {
            throw new AppError('Received non-array data from getAllPoems', 'UNEXPECTED');
          }

          const lowerCaseQuery = currentSearchQuery.trim().toLowerCase();
          fetchedPoems = allPoemsData.filter(poem => {
            if (!poem || !poem.id) return false;
            if (currentSearchType === 'title') {
              return poem.title?.toLowerCase().includes(lowerCaseQuery);
            } else if (currentSearchType === 'poet') {
              return poem.poet_name?.toLowerCase().includes(lowerCaseQuery);
            }
            return false;
          });
        }
        
        logger.info('Search effect successful', {
          resultCount: fetchedPoems.length
        });

        setSearchResults(fetchedPoems);

        // Optionally auto-select if only one result (might be desirable)
        if (fetchedPoems.length === 1) {
          handleSelectPoem(fetchedPoems[0]);
        } else if (fetchedPoems.length === 0) {
          toast({ title: "Not Found", description: "No poems matched your search criteria." });
        }
      } catch (error) {
        console.error('Search effect error:', error);
        const appError = handleError(error, 'performSearchEffect');
        logger.error('Search effect failed', appError, { searchType: currentSearchType, searchQuery: currentSearchQuery });
        toast({
          title: "Search Error",
          description: appError.message || `Failed to search for poems.`, 
          variant: "destructive"
        });
        setSearchResults([]); // Clear results on error
      } finally {
        setLoading(prev => ({ ...prev, search: false }));
      }
    };

    performSearch();

  // Depend on the trigger and the current search parameters
  }, [searchTrigger, currentSearchQuery, currentSearchType, toast]); // Add dependencies


  // --- MODIFIED: handleSearch function just updates state and triggers effect --- 
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() && searchType !== 'id') { // Allow empty ID search check in effect
      toast({
        title: "Search Error",
        description: "Please enter a search query.",
        variant: "destructive"
      });
      return;
    }
    
    // Update the parameters that the effect depends on
    setCurrentSearchQuery(searchQuery);
    setCurrentSearchType(searchType);
    
    // Reset pagination and trigger the search effect
    setCurrentPage(1); 
    setSearchTrigger(prev => prev + 1); // Increment trigger

  }, [searchQuery, searchType, toast]); // Dependencies for useCallback

  const getHighlightCount = async (poemId: string) => {
    setLoading(prev => ({ ...prev, fetchHighlightCount: true }));
    setCurrentHighlightCount(null);
    try {
      logger.info('Fetching highlight count via service', { poemId });

      const highlights = await getHighlightsByPoemId(poemId);

      let count = 0;
      if (Array.isArray(highlights)) {
         count = highlights.length;
      } else {
         logger.warning('Highlight data received was not an array', { poemId, data: highlights });
      }

      setCurrentHighlightCount(count);
      logger.info('Highlight count retrieved via service', {
        poemId,
        highlightCount: count
      });
    } catch (error) {
      console.error('Error getting highlight count via service:', error);
      if (error instanceof AppError && error.type === 'NOT_FOUND') {
          logger.info('No highlights found for poem (Caught NOT_FOUND)', { poemId });
          setCurrentHighlightCount(0);
      } else {
          logger.error('Failed to get highlight count via service', error, { poemId });
      }
    } finally {
       setLoading(prev => ({ ...prev, fetchHighlightCount: false }));
    }
  };

  const handleSelectPoem = async (poem: PoemDetails) => {
    logger.info('Poem selected', {
      poemId: poem.id,
      poemTitle: poem.title,
      poetName: poem.poet_name,
      poetId: poem.poet_id
    });
    
    setSelectedPoem(poem);
  };

  const {
    status: highlightGenerationStatus,
    error: highlightGenerationError,
    highlightCount,
    generateHighlights,
    clearStatus: clearHighlightStatus
  } = useHighlightGeneration('admin');

  const handleRegenerateHighlights = async () => {
    if (!selectedPoem || !selectedPoem.id || !selectedPoem.content) {
       toast({
        title: "No Poem Selected",
        description: "Please select a poem first.",
        variant: "destructive"
      });
      return;
    }

    setLoading(prev => ({ ...prev, regenerate: true }));
    clearHighlightStatus();
    const poemContent = selectedPoem.content.split('\n');

    await generateHighlights(
      selectedPoem.id,
      poemContent,
      selectedPoem.poet_name || 'Unknown Poet',
      poetStylePoints,
      aiProvider
    );
     setLoading(prev => ({ ...prev, regenerate: false }));
  };

  const handleDownloadLogs = () => {
    downloadHighlightLogs('admin');
  };

  // --- MODIFIED: handleDeletePoem to also trigger search refresh --- 
  const handleDeletePoem = async () => {
    if (!selectedPoem?.id) return;
    const poemIdToDelete = selectedPoem.id; // Store ID before state changes
    const currentTitle = selectedPoem.title; // Store title for messages

    if (!confirm(`Are you sure you want to permanently delete poem "${currentTitle}"? This cannot be undone.`)) {
      return;
    }
    
    setLoading(prev => ({ ...prev, deletePoem: true }));
    try {
      logger.info('Attempting to delete poem via service', { poemId: poemIdToDelete });
      await deletePoemById(poemIdToDelete);
      toast({ title: "Success", description: "Poem deleted successfully." });
      logger.info('Poem deleted successfully via service', { poemId: poemIdToDelete });

      // Clear selection and update local results immediately
      setSelectedPoem(null);
      setSearchResults(prev => prev.filter(p => p.id !== poemIdToDelete)); 
      
      // --- REMOVED: Trigger search refresh --- 
      // The local filter above is sufficient to update the UI immediately.
      // Cache invalidation in the service handles future search correctness.
      // setSearchTrigger(prev => prev + 1); 
      // Consider if pagination needs reset here too?
      // setCurrentPage(1); // Optional: Reset to page 1 after delete

    } catch (error) {
      const appError = handleError(error, 'handleDeletePoem');
      logger.error('Failed to delete poem', appError, { poemId: poemIdToDelete });
      toast({ 
        title: "Error Deleting Poem", 
        description: appError.message || 'Could not delete the poem. It might be referenced elsewhere.',
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, deletePoem: false }));
    }
  };
  
  const handleToggleArchive = async () => {
    // Modify condition: Only exit if essential IDs are missing.
    // Treat is_archived === null or undefined as false later.
    if (!selectedPoem?.id || !selectedPoem.poet_id) {
        // Add a log to see if this condition is met
        console.log('Exiting handleToggleArchive: Missing poem ID or poet ID.', { 
            poemId: selectedPoem?.id, 
            poetId: selectedPoem?.poet_id 
        });
        return;
    }
    
    setLoading(prev => ({ ...prev, toggleArchive: true }));
    
    // Determine current status, defaulting null/undefined to false
    const currentStatus = !!selectedPoem.is_archived; 
    const poemId = selectedPoem.id; // Known to exist
    const poetId = selectedPoem.poet_id; // Known to exist
    const subject = selectedPoem.subject;
    
    console.log('Proceeding with archive toggle:', { poemId, currentStatus });
    
    try {
      logger.info('Attempting to toggle archive status via service', { poemId, currentStatus });
      const updatedPoem = await togglePoemArchiveStatus(poemId, currentStatus, poetId, subject);
      
      // Update the selected poem state locally
      setSelectedPoem(prev => prev ? { ...prev, is_archived: updatedPoem.is_archived, archived_at: updatedPoem.archived_at } : null);
      // Update the poem in the search results list
      setSearchResults(prev => prev.map(p => p.id === poemId ? { ...p, is_archived: updatedPoem.is_archived, archived_at: updatedPoem.archived_at } : p));
      
      toast({ title: "Success", description: `Poem ${updatedPoem.is_archived ? 'archived' : 'unarchived'} successfully.` });
      logger.info('Poem archive status toggled successfully via service', { poemId, newStatus: updatedPoem.is_archived });
    } catch (error) {
      const appError = handleError(error, 'handleToggleArchive');
      logger.error('Failed to toggle archive status', appError, { poemId });
      toast({ 
        title: "Error Toggling Archive Status", 
        description: appError.message || 'Could not update archive status.',
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, toggleArchive: false }));
    }
  };
  
  const handleToggleProtect = async () => {
    // console.log('Protect button clicked!');
    // Log the values being checked
    // console.log('Checking condition:', {
    //   poemId: selectedPoem?.id,
    //   isProtectedValue: selectedPoem?.is_protected,
    // });
    
    // Modify condition: Only exit if poemId is missing.
    // Treat is_protected === null or undefined as false later.
    if (!selectedPoem?.id) {
      // console.log('Exiting handleToggleProtect: No selected poem ID.');
      return;
    }
    
    setLoading(prev => ({ ...prev, toggleProtect: true }));
    
    // Determine current status, defaulting null/undefined to false
    const currentStatus = !!selectedPoem.is_protected;
    const poemId = selectedPoem.id;
    
    // console.log('Proceeding with toggle:', { poemId, currentStatus });
    
    try {
      logger.info('Attempting to toggle protect status via service', { poemId, currentStatus });
      const updatedPoem = await togglePoemProtectStatus(poemId, currentStatus);
      
      // Update the selected poem state locally
      setSelectedPoem(prev => prev ? { ...prev, is_protected: updatedPoem.is_protected } : null);
      // Update the poem in the search results list
      setSearchResults(prev => prev.map(p => p.id === poemId ? { ...p, is_protected: updatedPoem.is_protected } : p));
      
      toast({ title: "Success", description: `Poem protection ${updatedPoem.is_protected ? 'enabled' : 'disabled'} successfully.` });
      logger.info('Poem protect status toggled successfully via service', { poemId, newStatus: updatedPoem.is_protected });
    } catch (error) {
      const appError = handleError(error, 'handleToggleProtect');
      logger.error('Failed to toggle protect status', appError, { poemId });
      toast({ 
        title: "Error Toggling Protect Status", 
        description: appError.message || 'Could not update protect status.',
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, toggleProtect: false }));
    }
  };
  // --- End Handlers ---

  // Log selected poem status just before rendering
  // console.log('Rendering component, selectedPoem?.is_protected:', selectedPoem?.is_protected);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Manage Poem Data</h1>
      
      {/* Calculate display results dynamically based on searchResults and excludeArchived */}
      {(() => {
        let displayResults = searchResults;
        if (searchType !== 'id' && excludeArchived) {
          displayResults = searchResults.filter(poem => !poem.is_archived);
        }
        
        // Calculate pagination based on displayResults
        const totalResults = displayResults.length;
        const totalPages = Math.ceil(totalResults / pageSize);
        const paginatedResults = displayResults.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        return (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Search for a Poem</CardTitle>
                <CardDescription>Find a poem to regenerate its highlights</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-col space-y-2">
                    <Label>Search By</Label>
                    <RadioGroup
                      value={searchType}
                      onValueChange={(value) => setSearchType(value as 'id' | 'title' | 'poet')}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="id" id="id" />
                        <Label htmlFor="id">Poem ID</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="title" id="title" />
                        <Label htmlFor="title">Poem Title</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="poet" id="poet" />
                        <Label htmlFor="poet">Poet Name</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      id="exclude-archived"
                      checked={excludeArchived}
                      onCheckedChange={setExcludeArchived}
                      disabled={searchType === 'id'}
                    />
                    <Label htmlFor="exclude-archived" className={searchType === 'id' ? 'text-muted-foreground' : ''}>
                      Exclude Archived Poems
                    </Label>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Input
                      placeholder={`Enter ${searchType === 'id' ? 'poem ID' : searchType === 'title' ? 'poem title' : 'poet name'}`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSearch}
                      disabled={loading.search}
                    >
                      {loading.search ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        'Search'
                      )}
                    </Button>
                  </div>
                  
                  {/* Use paginatedResults for display */}
                  {searchResults.length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-medium mb-2">Search Results ({totalResults} matching filter)</h3>
                      
                      {totalResults === 0 && (
                        <p className="text-sm text-muted-foreground">No poems match the current filter.</p>
                      )}

                      {/* Paginated List using paginatedResults */}
                      {paginatedResults.length > 0 && (
                        <ul className="space-y-2">
                          {paginatedResults.map((poem) => (
                            <li 
                              key={poem.id}
                              className={`p-2 border rounded ${selectedPoem?.id === poem.id ? 'border-primary bg-primary/5' : 'border-gray-200'}`}
                            >
                              <div className="flex justify-between items-center">
                                <div 
                                  onClick={() => handleSelectPoem(poem)}
                                  className="cursor-pointer flex-grow mr-4 hover:text-primary transition-colors duration-150"
                                >
                                  <div className="font-medium">{poem.title}</div>
                                  <div className="text-sm text-gray-500">by {poem.poet_name}</div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  asChild
                                  onClick={(e) => e.stopPropagation()}
                                  title="View Poem in Book"
                                >
                                  <Link to={`/book?poet=${encodeURIComponent(poem.poet_name || 'Unknown')}&poemId=${poem.id}`}>
                                    <Book className="h-4 w-4" />
                                  </Link>
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Pagination Controls using totalPages */}
                      {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            Page {currentPage} of {totalPages}
                          </span>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadLogs}
                >
                  Download Logs
                </Button>
              </CardFooter>
            </Card>
            
            {selectedPoem && (
              <Card>
                <CardHeader>
                  <CardTitle>Regenerate Highlights</CardTitle>
                  <CardDescription>
                    {selectedPoem.title || 'Untitled Poem'} by {selectedPoem.poet_name || 'Unknown Poet'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border rounded p-3 max-h-64 overflow-y-auto whitespace-pre-wrap">
                      {selectedPoem.content}
                    </div>
                    
                    {poetStylePoints.length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-medium mb-2">Poet Style Points ({poetStylePoints.length})</h3>
                        <ul className="list-disc pl-5 space-y-1">
                          {poetStylePoints.map((point, idx) => (
                            <li key={idx} className="text-sm">{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <Label className="font-medium mb-2 block">AI Provider</Label>
                      <RadioGroup
                        value={aiProvider}
                        onValueChange={(value) => setAiProvider(value as ApiProvider)}
                        className="flex space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={ApiProvider.ANTHROPIC} id="claude" />
                          <Label htmlFor="claude">Claude 3.7 Sonnet</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={ApiProvider.OPENAI} id="gpt" />
                          <Label htmlFor="gpt">GPT-4o</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between items-center flex-wrap gap-2">
                  {/* Regenerate Button */}
                  <Button
                    variant="destructive"
                    onClick={handleRegenerateHighlights}
                    disabled={highlightGenerationStatus === 'loading' || loading.regenerate}
                    className="flex-1 min-w-[180px]"
                  >
                    {highlightGenerationStatus === 'loading' || loading.regenerate ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      'Regenerate Highlights'
                    )}
                  </Button>
                  
                  {/* Poem Action Buttons */}
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleToggleArchive}
                      disabled={loading.toggleArchive}
                      title={selectedPoem?.is_archived ? 'Unarchive Poem' : 'Archive Poem'}
                    >
                      {loading.toggleArchive ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : selectedPoem?.is_archived ? (
                        <ArchiveRestore className="h-4 w-4" />
                      ) : (
                        <Archive className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleToggleProtect}
                      disabled={loading.toggleProtect}
                      title={selectedPoem?.is_protected ? 'Unprotect Poem' : 'Protect Poem'}
                    >
                      {loading.toggleProtect ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : selectedPoem?.is_protected ? (
                        <ShieldOff className="h-4 w-4" />
                      ) : (
                        <ShieldCheck className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleDeletePoem}
                      disabled={loading.deletePoem}
                      title="Delete Poem Permanently"
                      className="text-destructive hover:bg-destructive/10 border-destructive/50 hover:border-destructive/80"
                    >
                      {loading.deletePoem ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Highlight Generation Status - might need relocation or adjustment */}
                  <div className="w-full mt-2">
                    <HighlightGenerationStatus
                      status={highlightGenerationStatus}
                      error={highlightGenerationError}
                      context="admin"
                      highlightCount={highlightCount}
                      onRetry={handleRegenerateHighlights}
                      onDismiss={clearHighlightStatus}
                    />
                  </div>
                  
                  {/* View in Book Button - keep */} 
                  {highlightGenerationStatus === 'success' && selectedPoem && (
                    <Button 
                      variant="outline" 
                      asChild
                      className="mt-2"
                    >
                      <Link to={`/book?poet=${encodeURIComponent(selectedPoem.poet_name || 'Unknown')}&poemId=${selectedPoem.id}`}>
                        <Book className="mr-2 h-4 w-4" />
                        View in Book
                      </Link>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            )}
            {selectedPoem && currentHighlightCount !== null && (
              <p className="text-sm text-muted-foreground mt-2">
                Current highlight count: {loading.fetchHighlightCount
                  ? <Loader2 className="inline h-3 w-3 animate-spin" />
                  : currentHighlightCount
                }
              </p>
            )}
            {loading.fetchStylePoints && (
              <div className="flex items-center text-sm text-muted-foreground mt-2">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fetching style points...
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default AdminHighlightRegeneration; 